import React, { useState, useRef, useEffect } from 'react'
import SafeIcon from '../../common/SafeIcon'
import ImageLibraryModal from '../ImageLibrary/ImageLibraryModal'
import * as FiIcons from 'react-icons/fi'
import { useProjects } from '../../hooks/useProjects'
import { useAuth } from '../../contexts/AuthContext'

const { FiArrowLeft, FiSave, FiUpload, FiEdit3, FiFolder, FiX } = FiIcons

const ProjectSettings = ({ isOpen, project, onUpdateProject, onClose }) => {
  const { updateProject, uploadThumbnail } = useProjects()
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    title: project?.title || '',
    description: project?.description || '',
    tags: project?.tags || [],
    isPrivate: project?.is_private || false,
    thumbnail: project?.thumbnail_url || project?.computedThumbnail || null
  })
  const [loading, setLoading] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [thumbnailFile, setThumbnailFile] = useState(null)
  const [showImageLibrary, setShowImageLibrary] = useState(false)
  const thumbnailInputRef = useRef(null)

  // Load project data when modal opens
  React.useEffect(() => {
    if (isOpen && project) {
      setFormData({
        title: project.title || '',
        description: project.description || '',
        tags: project.tags || [],
        isPrivate: project.is_private || false,
        thumbnail: project.thumbnail_url || project.computedThumbnail || null
      })
    }
  }, [isOpen, project])

  if (!isOpen) return null

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setThumbnailFile(file)
      
      // Show preview
      const reader = new FileReader()
      reader.onload = (e) => {
        handleInputChange('thumbnail', e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleImageSelected = (selectedImage) => {
    // Use image from library as thumbnail
    handleInputChange('thumbnail', selectedImage.url)
    setThumbnailFile(null) // Clear file since we're using library image
    setShowImageLibrary(false)
  }

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      handleInputChange('tags', [...formData.tags, newTag.trim()])
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove) => {
    handleInputChange('tags', formData.tags.filter(tag => tag !== tagToRemove))
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      
      let thumbnailUrl = formData.thumbnail
      
      // Upload thumbnail if a new file was selected
      if (thumbnailFile) {
        const { data: uploadedUrl, error: uploadError } = await uploadThumbnail(project.id, thumbnailFile)
        if (uploadError) {
          console.error('Error uploading thumbnail:', uploadError)
          alert('Failed to upload thumbnail. Please try again.')
          return
        }
        thumbnailUrl = uploadedUrl
      }
      
      // Prepare update data (use database column names)
      const updateData = {
        title: formData.title,
        description: formData.description,
        tags: formData.tags,
        is_private: formData.isPrivate
      }
      
      // Always include thumbnail_url if we have one (whether uploaded or selected from library)
      if (thumbnailUrl) {
        updateData.thumbnail_url = thumbnailUrl
      }
      
      console.log('🔧 Updating project with data:', updateData)
      
      // Update project in database
      const { data, error } = await updateProject(project.id, updateData)
      
      if (error) {
        console.error('❌ Error updating project:', error)
        alert(`Failed to save project settings: ${error.message || 'Unknown error'}`)
        return
      }
      
      console.log('✅ Project updated successfully:', data)
      
      // Notify parent component
      onUpdateProject({
        ...formData,
        thumbnail_url: thumbnailUrl
      })
      
      alert('Project settings saved successfully!')
      onClose()
    } catch (err) {
      console.error('Error saving project settings:', err)
      alert('Failed to save project settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Project Settings</h1>
              <p className="text-sm text-gray-500">Configure your project details and appearance</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
              {/* Project Thumbnail */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Project Thumbnail</h3>
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    {formData.thumbnail ? (
                      <img 
                        src={formData.thumbnail} 
                        alt="Project thumbnail" 
                        className="w-32 h-20 rounded-lg border border-gray-300 object-cover"
                      />
                    ) : (
                      <div className="w-32 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                        <SafeIcon icon={FiEdit3} className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <button
                      onClick={() => thumbnailInputRef.current?.click()}
                      className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 rounded-lg transition-all flex items-center justify-center"
                    >
                      <SafeIcon icon={FiUpload} className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowImageLibrary(true)}
                      className="w-full px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiFolder} className="w-4 h-4" />
                      <span>Select from Library</span>
                    </button>
                    <button
                      onClick={() => thumbnailInputRef.current?.click()}
                      className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiUpload} className="w-4 h-4" />
                      <span>Upload Thumbnail</span>
                    </button>
                    <p className="text-sm text-gray-500 text-center">
                      Recommended: 800x450px (16:9 ratio)
                    </p>
                  </div>
                </div>
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                />
              </div>

              {/* Basic Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter project title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      rows={4}
                      placeholder="Describe your project..."
                    />
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tags</h3>
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add a tag..."
                    />
                    <button
                      onClick={addTag}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm flex items-center space-x-2"
                        >
                          <span>{tag}</span>
                          <button
                            onClick={() => removeTag(tag)}
                            className="text-gray-500 hover:text-red-500"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Privacy Settings */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Privacy</h3>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Private Project</p>
                    <p className="text-sm text-gray-600">
                      Only you can view and edit this project
                    </p>
                  </div>
                  <button
                    onClick={() => handleInputChange('isPrivate', !formData.isPrivate)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.isPrivate ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.isPrivate ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
          </div>
        </div>

        {/* Image Library Modal */}
        <ImageLibraryModal
          isOpen={showImageLibrary}
          onClose={() => setShowImageLibrary(false)}
          onImageSelected={handleImageSelected}
          projectTitle={formData.title}
          elementType="thumbnail"
        />
      </div>
    </div>
  )
}

export default ProjectSettings